import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AbilitiesService } from './abilities.service';
import { AgentCatalogService } from './agent-catalog.service';
import { ConnectionsService } from './connections.service';
import { environment } from '../../../environments/environment';

/**
 * Named hub navigation destinations — the single source of truth for "take me
 * to X" across the hub. What's New announcement CTAs resolve through here, and
 * the set of `action` ids below is exactly what the Admin portal's CTA-target
 * dropdown offers, so an admin can never pick a target the hub can't honor.
 *
 * A destination is either a router route (`route`) or a named in-app action
 * (`action`): a modal (abilities / agent catalog / connections) or an external
 * portal (Studio / Admin / Help, opened with the same session hand-off the
 * sidebar uses).
 */
export type HubActionId =
  | 'open-abilities'
  | 'open-catalog'
  | 'open-connections'
  | 'open-studio'
  | 'open-admin'
  | 'open-help';

@Injectable({ providedIn: 'root' })
export class HubDestinationsService {
  private readonly router = inject(Router);
  private readonly abilities = inject(AbilitiesService);
  private readonly catalog = inject(AgentCatalogService);
  private readonly connections = inject(ConnectionsService);

  /** Resolve a CTA — a route wins if present, else the named action. */
  go(target: { route?: string | null; action?: string | null }): void {
    if (target.route) {
      this.router.navigate([target.route]);
      return;
    }
    switch (target.action as HubActionId) {
      case 'open-abilities': this.abilities.triggerOpen(); break;
      case 'open-catalog': this.catalog.triggerOpen(); break;
      // Connections is a modal mounted in the layout shell. Opened by action
      // rather than the /connections route: that route exists only as the OAuth
      // return target and bounces to /chat, which would drop the user off the
      // surface they were on when they clicked the announcement.
      case 'open-connections': this.connections.triggerOpen(); break;
      case 'open-studio': this.openPortal(environment.STUDIO_PORTAL_URL); break;
      case 'open-admin': this.openPortal(environment.ADMIN_PORTAL_URL); break;
      case 'open-help': this.openPortal(environment.HELP_PORTAL_URL); break;
    }
  }

  /** Open Studio/Admin/Help in a new tab with the base64 session hand-off the
   *  portals expect on `#session=` (same payload the sidebar tiles build). */
  openPortal(baseUrl: string): void {
    if (!baseUrl) return;
    const payload = {
      'id-token': sessionStorage.getItem('id-token') ?? '',
      'current-role': sessionStorage.getItem('current-role') ?? '',
      'current-username': sessionStorage.getItem('current-username') ?? '',
      'current-email': sessionStorage.getItem('current-email') ?? '',
      'STATIC': sessionStorage.getItem('STATIC') ?? '',
    };
    const bytes = new TextEncoder().encode(JSON.stringify(payload));
    const encoded = btoa(Array.from(bytes, b => String.fromCodePoint(b)).join(''));
    window.open(`${baseUrl}/#session=${encoded}`, '_blank');
  }
}
